import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRightLeft, History, Lock, RefreshCw, Search, ShoppingCart } from 'lucide-react';
import { getConsolidatedStock } from '../services/api';
import HelpIcon from '../components/help/HelpIcon';
import TransferStockModal from '../components/TransferStockModal';
import { FEATURE_GATE_ERROR_CODE } from '../config/featureGates';
import { formatCurrency } from '../utils/formatCurrency';
import {
  STATUS_FILTERS,
  itemCells,
  filterConsolidatedItems,
  sortConsolidatedItems,
  shortageCount,
  suggestTransferSource,
  suggestTransferTarget,
  stockMapFromConsolidated,
  formatStock,
} from '../utils/inventoryConsolidated';
import './InventoryConsolidatedPage.css';

const SEARCH_DEBOUNCE_MS = 200;

/**
 * Tipo de error de carga: sin alcance de organización, fuera del plan o genérico.
 * @param {*} error - Error de axios
 * @returns {'scope'|'gate'|'generic'} Tipo
 */
function loadErrorKind(error) {
  const status = error?.response?.status;
  const data = error?.response?.data || {};
  if (status === 403 && data.error === FEATURE_GATE_ERROR_CODE) return 'gate';
  if (status === 403) return 'scope';
  return 'generic';
}

/**
 * Consolidado de stock insumo × local (RES-43, épica D1): matriz con semáforo por local, total
 * por insumo, filtros y accesos directos a transferir o pedir. Solo para quien administra toda la
 * organización (el backend responde 403 `ORG_SCOPE_REQUIRED` al resto).
 * @returns {JSX.Element} Página
 */
function InventoryConsolidatedPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('shortages');
  const [transfer, setTransfer] = useState(null); // props iniciales del modal
  const [feedback, setFeedback] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getConsolidatedStock();
      setData(response || { restaurants: [], items: [] });
      setLoadError(null);
    } catch (error) {
      console.error('Error loading consolidated stock:', error?.response?.data || error);
      setLoadError(loadErrorKind(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(''), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const restaurants = useMemo(() => data?.restaurants || [], [data]);
  const categories = useMemo(() => data?.categories || [], [data]);
  const rows = useMemo(
    () =>
      sortConsolidatedItems(
        filterConsolidatedItems(data?.items || [], { search, category, status }),
        sort
      ),
    [data, search, category, status, sort]
  );
  const stockByRestaurant = useMemo(() => stockMapFromConsolidated(data), [data]);
  const counts = data?.summary?.counts || {};

  const openTransferFor = (item) => {
    const target = suggestTransferTarget(item, restaurants);
    const source = target ? suggestTransferSource(item, target) : null;
    setTransfer({
      initialFromRestaurantId: source || '',
      initialToRestaurantId: target || '',
      initialItems: [{ itemId: item.itemId }],
    });
  };

  const handleTransferred = () => {
    setFeedback(t('inventory.consolidated.transferDone'));
    load();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setCategory('');
    setStatus('all');
  };

  const hasFilters = Boolean(searchInput || category || status !== 'all');

  const renderEmptyState = (kind) => (
    <div className="consolidated-empty" data-testid={`consolidated-empty-${kind}`}>
      {kind === 'scope' && <Lock size={32} aria-hidden="true" />}
      <h2>{t(`inventory.consolidated.empty.${kind}Title`)}</h2>
      <p>{t(`inventory.consolidated.empty.${kind}Description`)}</p>
      {kind === 'generic' && (
        <button type="button" className="btn btn-secondary" onClick={load}>
          <RefreshCw size={16} aria-hidden="true" />
          {t('common.retry')}
        </button>
      )}
    </div>
  );

  const renderCell = (cell, item, restaurantId) => {
    if (!cell) {
      return (
        <td key={restaurantId} className="num consolidated-cell consolidated-cell--none">
          —
        </td>
      );
    }
    const title = t('inventory.consolidated.cellTitle', {
      status: t(`inventory.consolidated.status.${cell.status}`),
      min: cell.minStock === null || cell.minStock === undefined ? '—' : formatStock(cell.minStock),
    });
    return (
      <td
        key={restaurantId}
        className={`num consolidated-cell consolidated-cell--${cell.status}`}
        title={title}
        data-testid={`cell-${item.itemId}-${restaurantId}`}
        data-status={cell.status}
      >
        <span className="consolidated-dot" aria-hidden="true" />
        <span className="consolidated-stock">{formatStock(cell.stock)}</span>
        <span className="sr-only">{t(`inventory.consolidated.status.${cell.status}`)}</span>
      </td>
    );
  };

  const renderTable = () => {
    if ((data?.items || []).length === 0) {
      return renderEmptyState('noItems');
    }
    return (
      <>
        <div className="consolidated-filters">
          <div className="consolidated-search">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('inventory.consolidated.searchPlaceholder')}
              aria-label={t('inventory.consolidated.searchPlaceholder')}
              data-testid="consolidated-search"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label={t('inventory.consolidated.filters.category')}
            data-testid="consolidated-category"
          >
            <option value="">{t('inventory.consolidated.filters.allCategories')}</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label={t('inventory.consolidated.filters.status')}
            data-testid="consolidated-status"
          >
            {STATUS_FILTERS.map((value) => (
              <option key={value} value={value}>
                {t(`inventory.consolidated.filters.statusOptions.${value}`)}
              </option>
            ))}
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label={t('inventory.consolidated.filters.sort')}
            data-testid="consolidated-sort"
          >
            <option value="shortages">{t('inventory.consolidated.filters.sortShortages')}</option>
            <option value="name">{t('inventory.consolidated.filters.sortName')}</option>
          </select>
          {hasFilters && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={clearFilters}>
              {t('inventory.consolidated.filters.clear')}
            </button>
          )}
        </div>

        {rows.length === 0 ? (
          <p className="consolidated-muted consolidated-no-results">
            {t('inventory.consolidated.noResults')}
          </p>
        ) : (
          <div className="consolidated-table-wrapper" data-testid="consolidated-table-wrapper">
            <table className="consolidated-table">
              <thead>
                <tr>
                  <th className="consolidated-col-item">
                    {t('inventory.consolidated.columns.item')}
                  </th>
                  {restaurants.map((r) => (
                    <th
                      key={r.restaurantId}
                      className="num"
                      data-testid="consolidated-restaurant-col"
                    >
                      {r.name || r.restaurantId}
                    </th>
                  ))}
                  <th className="num consolidated-col-total" data-testid="consolidated-total-col">
                    {t('inventory.consolidated.columns.total')}
                  </th>
                  <th className="consolidated-col-actions">
                    <span className="sr-only">{t('inventory.consolidated.columns.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => {
                  const shortages = shortageCount(item);
                  return (
                    <tr key={item.itemId} data-testid={`consolidated-row-${item.itemId}`}>
                      <th scope="row" className="consolidated-col-item">
                        <span className="consolidated-item-name">{item.name}</span>
                        <span className="consolidated-item-meta">
                          {[item.category, item.unit].filter(Boolean).join(' · ')}
                        </span>
                      </th>
                      {itemCells(item, restaurants).map((cell, index) =>
                        renderCell(cell, item, restaurants[index].restaurantId)
                      )}
                      <td
                        className={`num consolidated-col-total consolidated-cell--${
                          item.total?.worstStatus || 'none'
                        }`}
                        data-testid={`total-${item.itemId}`}
                      >
                        <strong>
                          {formatStock(item.total?.stock)} {item.unit || ''}
                        </strong>
                        {item.total?.value !== undefined && item.total?.value !== null && (
                          <span className="consolidated-item-meta">
                            {formatCurrency(item.total.value)}
                          </span>
                        )}
                      </td>
                      <td className="consolidated-col-actions">
                        <div className="consolidated-row-actions">
                          {restaurants.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => openTransferFor(item)}
                              title={t('inventory.consolidated.actions.transfer')}
                              data-testid={`transfer-${item.itemId}`}
                            >
                              <ArrowRightLeft size={14} aria-hidden="true" />
                              <span className="consolidated-action-label">
                                {t('inventory.consolidated.actions.transfer')}
                              </span>
                            </button>
                          )}
                          {shortages > 0 && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => navigate('/inventory/purchase-orders/new')}
                              title={t('inventory.consolidated.actions.purchaseOrderHint')}
                              data-testid={`po-${item.itemId}`}
                            >
                              <ShoppingCart size={14} aria-hidden="true" />
                              <span className="consolidated-action-label">
                                {t('inventory.consolidated.actions.purchaseOrder')}
                              </span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  const renderBody = () => {
    if (loading && !data && !loadError) {
      return <p className="consolidated-muted">{t('common.loading')}</p>;
    }
    if (loadError) return renderEmptyState(loadError);
    return (
      <>
        <div className="consolidated-summary">
          <div className="consolidated-card">
            <span className="consolidated-card-label">
              {t('inventory.consolidated.summary.restaurants')}
            </span>
            <strong data-testid="summary-restaurants">
              {data?.summary?.restaurantCount ?? restaurants.length}
            </strong>
          </div>
          <div className="consolidated-card">
            <span className="consolidated-card-label">
              {t('inventory.consolidated.summary.items')}
            </span>
            <strong>{data?.summary?.itemCount ?? (data?.items || []).length}</strong>
          </div>
          {['negative', 'out', 'low', 'ok'].map((key) => (
            <button
              type="button"
              key={key}
              className={`consolidated-card consolidated-card--${key}${
                status === key ? ' is-active' : ''
              }`}
              onClick={() => setStatus(status === key ? 'all' : key)}
              aria-pressed={status === key}
              data-testid={`summary-${key}`}
            >
              <span className="consolidated-card-label">
                <span className="consolidated-dot" aria-hidden="true" />
                {t(`inventory.consolidated.status.${key}`)}
              </span>
              <strong>{counts[key] ?? 0}</strong>
            </button>
          ))}
          {data?.summary?.value !== undefined && (
            <div className="consolidated-card">
              <span className="consolidated-card-label">
                {t('inventory.consolidated.summary.value')}
              </span>
              <strong>{formatCurrency(data.summary.value)}</strong>
            </div>
          )}
        </div>
        {renderTable()}
      </>
    );
  };

  const canTransfer = !loadError && restaurants.length > 1;

  return (
    <div className="inventory-consolidated-page">
      <div className="consolidated-header">
        <div>
          <h1>
            {t('inventory.consolidated.title')}
            <HelpIcon topicKey="inventory.consolidated" />
          </h1>
          <p className="consolidated-subtitle">{t('inventory.consolidated.subtitle')}</p>
        </div>
        <div className="consolidated-header-actions">
          <Link to="/inventory/transfers" className="btn btn-ghost">
            <History size={16} aria-hidden="true" />
            {t('inventory.consolidated.actions.history')}
          </Link>
          {canTransfer && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setTransfer({})}
              data-testid="consolidated-new-transfer"
            >
              <ArrowRightLeft size={16} aria-hidden="true" />
              {t('inventory.consolidated.actions.newTransfer')}
            </button>
          )}
        </div>
      </div>

      {feedback && (
        <div className="alert alert-success" role="status">
          {feedback}
        </div>
      )}

      {renderBody()}

      {transfer && (
        <TransferStockModal
          restaurants={restaurants}
          stockByRestaurant={stockByRestaurant}
          onClose={() => setTransfer(null)}
          onTransferred={handleTransferred}
          {...transfer}
        />
      )}
    </div>
  );
}

export default InventoryConsolidatedPage;
